import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getDefaultDateRange } from "./utils/datePresets";
import { DateRangeSelector } from "./components/DateRangeSelector";
import { HoursListView } from "./components/HoursListView";
import { LocationsListView } from "./components/LocationsListView";
import { LocationDetailView } from "./components/LocationDetailView";
import { RunHoursView } from "./components/RunHoursView";
import { DestinationFetchRunImproved, useListDestinationRuns } from "@/lib/api/destinationHoursImproved";
import { useQueryParam } from "@/hooks/use-query-param";
import moment from "moment";

export function DestinationHoursImprovedDashboard() {
  const defaultDateRange = getDefaultDateRange();
  
  // URL query params for persistence
  const [activeTab, setActiveTab] = useQueryParam("tab", "hours");
  const [selectedLocationId, setSelectedLocationId] = useQueryParam("location_id", "");
  const [selectedRunId, setSelectedRunId] = useQueryParam("run_id", "");
  const [dateFromParam, setDateFromParam] = useQueryParam(
    "date_from", 
    defaultDateRange.from ? moment(defaultDateRange.from).format("YYYY-MM-DD") : ""
  );
  const [dateToParam, setDateToParam] = useQueryParam(
    "date_to", 
    defaultDateRange.to ? moment(defaultDateRange.to).format("YYYY-MM-DD") : ""
  );
  
  const [searchParams, setSearchParams] = useSearchParams();

  // Convert query params back to date objects
  const dateRange = useMemo(() => {
    return {
      from: dateFromParam ? moment(dateFromParam).toDate() : defaultDateRange.from,
      to: dateToParam ? moment(dateToParam).toDate() : defaultDateRange.to,
    };
  }, [dateFromParam, dateToParam, defaultDateRange]);

  // Fetch run data when run_id is present
  const runsQuery = useListDestinationRuns(
    selectedLocationId || "",
    { limit: 500 }
  );
  
  const [selectedRun, setSelectedRun] = useState<DestinationFetchRunImproved | null>(null);
  
  // Update selectedRun when run data is fetched or when URL params change
    /* useEffect(() => {
  *   if (selectedRunId && selectedLocationId && runsQuery.data?.data) {
  *     const run = runsQuery.data.data.find((r: DestinationFetchRunImproved) => r.id === selectedRunId);
  *     setSelectedRun(run || null);
  *   } else if (!selectedRunId) {
  *     setSelectedRun(null);
  *   }
  * }, [selectedRunId, selectedLocationId, runsQuery.data]);
  *  */

  // Handle deep linking: ensure correct tab is set based on URL params
  useEffect(() => {
    console.warn("se", selectedRunId, selectedLocationId, activeTab)
    if (selectedRunId && selectedLocationId && !activeTab.includes("run-hours")) {
      setActiveTab("run-hours");
    } else if (selectedLocationId && !selectedRunId && !activeTab.includes("location-detail")) {
      setActiveTab("location-detail");
    } else if (!selectedLocationId && !selectedRunId && activeTab !== "hours" && activeTab !== "locations") {
      setActiveTab("hours");
    }
  }, [selectedLocationId, selectedRunId, activeTab, setActiveTab]);

  const handleLocationSelect = (locationId: string) => {
    setSearchParams((prev: URLSearchParams) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("tab", "location-detail");
      newParams.set("location_id", locationId);
      return newParams;
    });
  };

  const handleBackToLocations = () => {
    // Clear location and run related params
    setSearchParams((prev: URLSearchParams) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("location_id");
      newParams.delete("run_id");
      newParams.delete("service_category_id");
      newParams.delete("run_status");
      newParams.set("tab", "locations");
      return newParams;
    });
  };

  const handleRunSelect = (run: DestinationFetchRunImproved) => {
    setSearchParams((prev: URLSearchParams) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("tab", "run-hours");
      newParams.set("run_id", run.id);
      return newParams;
    });
  };

  const handleBackToLocationDetail = () => {
    setSearchParams((prev: URLSearchParams) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("tab", "location-detail");
      newParams.delete("run_id");
      return newParams;
    });
  };

  const handleDateRangeChange = (newRange: { from?: Date; to?: Date }) => {
    setDateFromParam(newRange.from ? moment(newRange.from).format("YYYY-MM-DD") : "");
    setDateToParam(newRange.to ? moment(newRange.to).format("YYYY-MM-DD") : "");
  };

  const handleTabChange = (tabValue: string) => {
    setSearchParams((prev: URLSearchParams) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("tab", tabValue);
      
      // Clear params that don't apply to the selected tab
      if (tabValue === "hours" || tabValue === "locations") {
        newParams.delete("location_id");
        newParams.delete("run_id");
        newParams.delete("service_category_id");
        newParams.delete("run_status");
      } else if (tabValue === "location-detail") {
        newParams.delete("run_id");
      }
      
      return newParams;
    });
  };

  const tabsConfig = useMemo(() => {
    const tabs = [
      { value: "hours", label: "Hours List" },
      { value: "locations", label: "Locations" },
    ];
    
    if (selectedLocationId) {
      tabs.push({ value: "location-detail", label: "Location Detail" });
    }
    
    if (selectedRunId && selectedRun) {
      tabs.push({ value: "run-hours", label: "Run Hours" });
    }
    
    return tabs;
  }, [selectedLocationId, selectedRunId, selectedRun]);

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <h1 className="text-3xl font-bold">Destination Hours Improved Dashboard</h1>
        
        {/* Date Range Filter - only show for hours tab */}
        {activeTab === "hours" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Date Range</CardTitle>
            </CardHeader>
            <CardContent>
              <DateRangeSelector
                value={dateRange}
                onChange={handleDateRangeChange}
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabs for different views */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${tabsConfig.length}, minmax(0, 1fr))` }}>
          {tabsConfig.map(tab => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        
        <TabsContent value="hours" className="space-y-4">
            <HoursListView dateRange={dateRange} />
        </TabsContent>
        
        <TabsContent value="locations" className="space-y-4">
            <LocationsListView onLocationSelect={handleLocationSelect} />
        </TabsContent>
        
        {selectedLocationId && (
          <TabsContent value="location-detail" className="space-y-4">
            <LocationDetailView
              locationId={selectedLocationId}
              onBackClick={handleBackToLocations}
              onRunSelect={handleRunSelect}
            />
          </TabsContent>
        )}
        
        {selectedRunId && (
          <TabsContent value="run-hours" className="space-y-4">
            <RunHoursView
              onBackClick={handleBackToLocationDetail}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
